// Note: Customized built-in element support (https://developer.mozilla.org/en-US/docs/Web/API/Web_components/Using_custom_elements)
// can not be used for now, because it is not supported by some of the modern browsers:
// https://caniuse.com/mdn-api_customelementregistry_builtin_element_support

import { html, render, nothing } from 'lit-html';
import { addTranslation, determineTranslation } from "../../utils/language.js";

export abstract class DialogBase extends HTMLElement {

    private dialog: HTMLDialogElement;

    constructor() {
        super();

        this.addTranslation("en", [{name:'close', value:'Close'}])
        this.addTranslation("no", [{name:'close', value:'Lukk'}])
    }

    // #region Markup
    public titleTemplate: () => any = () => null;
    public bodyTemplate: () => any = () => null;
    public buttonsTemplate: () => any = () => null;
    // #endregion

    // #region Protected
    protected async onBeforeOpen(): Promise<void> {};
    protected async onBeforeClose(): Promise<void> {};
    protected async configure(): Promise<void> {};
    // #endregion

    // #region Translations

    protected translations = {};
    protected dialogLabels: { [key: string]:any };
    public addTranslation(locale: string, localValues: {name: string; value: string}[]): void {
        addTranslation(this.translations, locale, localValues);
        this.dialogLabels = determineTranslation(this.translations);
    }

    //#endregion

    // #region Public
    public async show(): Promise<void> {
        await this.onBeforeOpen();
        await this.renderView();
        await this.adjustBeforeOpen();
        this.dialog.show();
    }

    public async showModal(): Promise<void> {
        await this.onBeforeOpen();
        await this.renderView();
        await this.adjustBeforeOpen();
        this.dialog.showModal();
    }

    public async close(returnValue?: string): Promise<void> {
        if (!this.dialog || !this.dialog.open) {
            return;
        }

        if (!returnValue || !returnValue.length) {
            this.dialog.returnValue = "";
        }

        await this.onBeforeClose();
        await this.adjustBeforeClose();
        this.dialog.close(returnValue);
    }

    private async onDialogInternalClose() {
        await this.onBeforeClose();
        await this.adjustBeforeClose();
    }

    public get returnValue() {
        return this.dialog?.returnValue;
    }

    public set returnValue(val: string) {
        if (this.dialog) {
            this.dialog.returnValue = val;
        }
    }
    // #endregion

    // #region Private
    private template = (): any => {
        const titleTemplate = this.titleTemplate();
        const buttonsTemplate = this.buttonsTemplate();

        const template = html`
            <div class="dialog-header">
                ${titleTemplate
                    ? html`${titleTemplate}`
                    : html`<h4 class="dialog-title">${this.title}</h4>`}
                <button type="button" class="btn btn-icon dialog-btn-close" title="${this.dialogLabels.close}"
                    @click=${this.handleClose.bind(this)}><span>${this.dialogLabels.close}</span>
                </button>
            </div>
            <div class="dialog-body">
                <div class="dialog-content">
                    ${this.bodyTemplate()}
                </div>
            </div>
            <div class="dialog-footer">
                ${buttonsTemplate
                    ? html`${buttonsTemplate}`
                    : nothing}
                </div>
            </div>`

        return template;
    };

    private async renderView(): Promise<void> {
        render(this.template(), this.dialog);
        await this.configure();
    }

    private async adjustBeforeOpen(): Promise<void>{
        document.body.style.overflow = "hidden";
        this.dialog.classList.remove('d-none');
        this.dialog.classList.add('d-flex');
    }

    private async adjustBeforeClose(): Promise<void>{
        document.body.style.removeProperty("overflow");
        this.dialog.classList.remove('d-flex');
        this.dialog.classList.add('d-none');
    }

    // #endregion

    // #region Event handlers
    private async handleClose() {
        setTimeout(() => this.close(), 100);
    }
    // #endregion

    connectedCallback() {
        this.dialog = document.createElement("dialog");
        this.dialog.classList.add("cms-dialog");
        this.dialog.classList.add("flex-column");
        this.dialog.addEventListener("close", this.onDialogInternalClose.bind(this));
        document.body.appendChild(this.dialog);
    }
}
